import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Typography,
  useTheme
} from '@mui/material'
import { useAuth } from 'src/contexts/AuthContext'
import { getSiteOrigin } from 'src/utils/siteUrl'
import { isTeamFeatureEnabled } from 'src/utils/featureFlags'
import { doc, Timestamp } from 'firebase/firestore'
import { useDocumentData } from 'react-firebase-hooks/firestore'
import { firestore } from 'src/shims/firebase'
import {
  createCustomerPortalSession,
  createGroupCheckoutSession,
  retrieveStripePrice,
  syncGroupSubscriptionFromStripe
} from '@batical/common'
import { selectSubscription, useIsSubscriber } from '@batical/reducer/src'
import { format, getUnixTime } from 'date-fns'
import { fr } from 'date-fns/locale'
import GroupIcon from '@mui/icons-material/Group'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone'
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import EventIcon from '@mui/icons-material/Event'
import PersonAddIcon from '@mui/icons-material/PersonAdd'

// ---------------------------------------------------------------------------
// Types & helpers (pricing)
// ---------------------------------------------------------------------------

type TierLike = { up_to?: number | null; unit_amount?: number | null; unit_amount_decimal?: string | null }
type PriceDisplay = {
  id: string
  unit_amount?: number | null
  currency?: string
  recurring?: { interval?: string; interval_count?: number }
  tiers?: Array<TierLike>
}
type StripeSubDoc = {
  status?: string
  quantity?: number
  items?: Array<{
    quantity?: number
    price?: {
      unit_amount?: number | null
      currency?: string
      recurring?: { interval?: string; interval_count?: number }
      product?: string | { name?: string; id?: string }
      tiers?: Array<TierLike>
    }
  }>
  current_period_end?: Timestamp | { seconds: number }
  cancel_at_period_end?: boolean
}

function firestoreDate(ts: Timestamp | { seconds: number } | undefined | null): Date | null {
  if (!ts) return null
  if (ts instanceof Timestamp) return ts.toDate()
  if (typeof (ts as { seconds?: number }).seconds === 'number') return new Date((ts as { seconds: number }).seconds * 1000)
  return null
}

function tierUnitCents(t: TierLike): number | null {
  if (t.unit_amount != null && Number.isFinite(Number(t.unit_amount))) return Number(t.unit_amount)
  if (t.unit_amount_decimal != null && t.unit_amount_decimal !== '') {
    const n = Number(t.unit_amount_decimal)
    return Number.isFinite(n) ? Math.round(n * 100) : null
  }
  return null
}


const STRIPE_STATUS_FR: Record<string, string> = {
  active: 'Actif',
  canceled: 'Annulé',
  incomplete: 'Incomplet',
  incomplete_expired: 'Expiré',
  past_due: 'Paiement en retard',
  trialing: "Période d'essai",
  unpaid: 'Impayé',
  paused: 'En pause'
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SubscriptionPage() {
  const theme = useTheme()
  const navigate = useNavigate()
  const { user } = useAuth()
  const uid = user?.uid ?? ''
  const subscription = useSelector(selectSubscription)
  const isSubscriber = useIsSubscriber()
  const nowUnix = getUnixTime(new Date())

  // --- Firestore data ---
  const [groupSub, groupSubLoading] = useDocumentData(
    uid ? doc(firestore, 'subscription', uid, 'groupSubscription', 'data') : null
  )
  const groupStripeSubId = (groupSub as any)?.stripeSubscriptionId as string | undefined
  const groupStatus = (groupSub as any)?.status as string | undefined

  const [stripeSubDoc, stripeSubLoading] = useDocumentData(
    groupStripeSubId ? doc(firestore, 'stripeSubscriptions', groupStripeSubId) : null
  )

  const [stripeUser, stripeUserLoading] = useDocumentData(uid ? doc(firestore, 'stripe', uid) : null)
  const [stripeDefault, stripeDefaultLoading] = useDocumentData(doc(firestore, 'stripe', 'default'))
  const rawPriceId = (stripeUser as any)?.priceId ?? (stripeDefault as any)?.priceId
  const effectivePriceId: string | undefined = Array.isArray(rawPriceId) ? rawPriceId[0] : rawPriceId

  // --- Subscription sources ---
  const hasWebSub = (subscription.expirationDateStripe ?? 0) > nowUnix
  const hasMobileSub =
    (subscription.expirationDate ?? 0) > nowUnix &&
    (subscription.isIos === true || subscription.isAndroid === true)
  const hasDistributorSub =
    subscription.isDistributor === true && (subscription.expirationDateDistributor ?? 0) > nowUnix

  // --- Sync Firestore from Stripe if data is missing ---
  useEffect(() => {
    if (!uid || groupSubLoading) return
    const hasGroupSubId = Boolean((groupSub as any)?.stripeSubscriptionId)
    if (hasWebSub && !hasGroupSubId) {
      syncGroupSubscriptionFromStripe().catch((e) => console.error('sync failed', e))
    }
  }, [uid, groupSubLoading, groupSub, hasWebSub])

  // --- Stripe subscription details ---
  const stripeSub = useMemo(() => {
    if (!groupStripeSubId || !stripeSubDoc) return null
    const d = stripeSubDoc as StripeSubDoc
    const item = d.items?.[0]
    const price = item?.price
    const qty = d.quantity ?? item?.quantity ?? 0
    const renew = firestoreDate(d.current_period_end)
    return { status: d.status ?? groupStatus, quantity: qty, renew, cancelAtPeriodEnd: d.cancel_at_period_end === true, price }
  }, [groupStripeSubId, stripeSubDoc, groupStatus])

  // --- Seat count ---
  const seatDisplay = useMemo(() => {
    if (!groupStripeSubId) return null
    const billed = stripeSub?.quantity ?? (groupSub as any)?.quantity ?? 0
    const subs = (groupSub as any)?.subscribers as Array<{ active?: boolean }> | undefined
    const used = Array.isArray(subs) ? subs.filter(s => s.active !== false).length : 0
    return { used, billed, free: Math.max(billed - used, 0) }
  }, [groupStripeSubId, groupSub, stripeSub])

  // --- Pricing ---
  const [prices, setPrices] = useState<PriceDisplay[]>([])
  const [pricesFetched, setPricesFetched] = useState(false)
  useEffect(() => {
    if (!effectivePriceId) return
    setPricesFetched(false)
    console.log('[SubscriptionPage] retrieveStripePrice → calling', { uid, effectivePriceId, rawPriceId, stripeUser, stripeDefault })
    retrieveStripePrice([effectivePriceId])
      .then(p => {
        console.log('[SubscriptionPage] retrieveStripePrice ← OK', { count: p?.length, prices: p })
        setPrices(p as unknown as PriceDisplay[])
      })
      .catch(err => {
        console.error('[SubscriptionPage] retrieveStripePrice ← ERR', err)
      })
      .finally(() => setPricesFetched(true))
  }, [effectivePriceId])
  const loadingPrices =
    stripeUserLoading || stripeDefaultLoading || (Boolean(effectivePriceId) && !pricesFetched)

  const tierDisplay = useMemo(() => {
    const p = prices[0]
    if (!p?.tiers?.length) return null
    const sym = (p.currency ?? 'eur').toUpperCase() === 'EUR' ? '\u20ac' : (p.currency ?? 'EUR').toUpperCase()
    const interval = p.recurring?.interval === 'year' ? 'an' : p.recurring?.interval === 'month' ? 'mois' : (p.recurring?.interval ?? 'mois')
    return {
      rows: p.tiers.map((tier, i) => ({
        label: tier.up_to != null
          ? `Jusqu'a ${tier.up_to} membre${(tier.up_to ?? 0) > 1 ? 's' : ''}`
          : `A partir de ${((p.tiers ?? [])[i - 1]?.up_to ?? 0) + 1} membres`,
        price: `${((tierUnitCents(tier) ?? 0) / 100).toFixed(2)} ${sym} / ${interval} / membre`
      })),
      headline: prices[0]?.tiers?.[0]
        ? `${((tierUnitCents(prices[0].tiers[0]) ?? 0) / 100).toFixed(2)} ${sym} / ${interval} / membre`
        : null
    }
  }, [prices])

  // --- Actions ---
  const [portalLoading, setPortalLoading] = useState(false)
  const [checkoutLoading, setCheckoutLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const openPortal = useCallback(async () => {
    setPortalLoading(true)
    setError(null)
    console.log('[SubscriptionPage] openPortal → calling', { uid, returnUrl: getSiteOrigin() + '/subscription' })
    try {
      const { url } = await createCustomerPortalSession(getSiteOrigin() + '/subscription')
      console.log('[SubscriptionPage] openPortal ← OK', { url })
      window.location.href = url
    } catch (e: any) {
      console.error('[SubscriptionPage] openPortal ← ERR', e)
      setError(e?.message || "Impossible d'ouvrir le portail de facturation.")
      setPortalLoading(false)
    }
  }, [uid])

  const subscribeMyself = useCallback(async () => {
    console.log('[SubscriptionPage] subscribeMyself → entry', { uid, email: user?.email, effectivePriceId })
    if (!effectivePriceId || !user?.email) {
      console.warn('[SubscriptionPage] subscribeMyself ABORT — missing priceId or email', { effectivePriceId, email: user?.email })
      return
    }
    setCheckoutLoading(true)
    setError(null)
    const payload = {
      ownerUid: uid,
      baseUrl: getSiteOrigin(),
      priceId: effectivePriceId,
      subscribers: [{ mail: user.email }]
    }
    console.log('[SubscriptionPage] createGroupCheckoutSession → calling', payload)
    try {
      const { url } = await createGroupCheckoutSession(payload)
      console.log('[SubscriptionPage] createGroupCheckoutSession ← OK', { url })
      window.location.href = url
    } catch (e: any) {
      console.error('[SubscriptionPage] createGroupCheckoutSession ← ERR', e)
      setError(e?.message || 'Impossible de demarrer le paiement.')
      setCheckoutLoading(false)
    }
  }, [effectivePriceId, uid, user?.email])

  // --- Render ---
  const loading = groupSubLoading || stripeSubLoading

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', py: 2 }}>
      <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mb: 3 }}>
        Abonnement
      </Typography>

      {/* ============================================================= */}
      {/* 1. STATUS BANNER                                              */}
      {/* ============================================================= */}
      <Card sx={{ mb: 3, border: isSubscriber ? '1px solid rgba(53,216,156,0.3)' : '1px solid rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {isSubscriber ? (
            <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 28 }} />
          ) : (
            <CancelOutlinedIcon sx={{ color: 'text.disabled', fontSize: 28 }} />
          )}
          <Typography variant="h6" sx={{ fontWeight: 700, color: isSubscriber ? '#0f172a' : '#94a3b8' }}>
            {isSubscriber ? "Vous etes abonne" : "Vous n'etes pas abonne"}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {hasWebSub && <Chip label="Web" size="small" color="success" variant="outlined" sx={{ fontWeight: 600 }} />}
            {hasMobileSub && (
              <Chip icon={<PhoneIphoneIcon />} label={subscription.isIos ? 'iOS' : 'Android'} size="small" color="info" variant="outlined" sx={{ fontWeight: 600 }} />
            )}
            {hasDistributorSub && (
              <Chip icon={<CardGiftcardIcon />} label="Licence" size="small" color="secondary" variant="outlined" sx={{ fontWeight: 600 }} />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* ============================================================= */}
      {/* 2. WEB SUBSCRIPTION DETAILS (si groupStripeSubId)             */}
      {/* ============================================================= */}
      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <CircularProgress size={20} />
          <Typography sx={{ color: '#64748b' }}>Chargement...</Typography>
        </Box>
      )}

      {stripeSub && (
        <Card sx={{ mb: 3, border: '1px solid rgba(0,0,0,0.06)' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <ReceiptLongIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Mon abonnement web</Typography>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Chip
                size="small"
                label={STRIPE_STATUS_FR[stripeSub.status ?? ''] ?? stripeSub.status ?? '--'}
                color={stripeSub.status === 'active' ? 'success' : 'default'}
                sx={{ fontWeight: 700 }}
              />
              {stripeSub.cancelAtPeriodEnd && (
                <Chip size="small" label="Resiliation en fin de periode" color="warning" sx={{ fontWeight: 600 }} />
              )}
            </Box>

            {seatDisplay && (
              <Typography sx={{ color: '#475569', mb: 1 }}>
                <strong>{seatDisplay.used}</strong> siege{seatDisplay.used > 1 ? 's' : ''} utilise{seatDisplay.used > 1 ? 's' : ''}
                {' sur '}
                <strong>{seatDisplay.billed}</strong> facture{seatDisplay.billed > 1 ? 's' : ''}
                {seatDisplay.free > 0 && (
                  <> — <strong>{seatDisplay.free}</strong> libre{seatDisplay.free > 1 ? 's' : ''}</>
                )}
              </Typography>
            )}

            {stripeSub.renew && !Number.isNaN(stripeSub.renew.getTime()) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#475569', mb: 2 }}>
                <EventIcon sx={{ fontSize: 18 }} />
                <Typography variant="body2">
                  Fin de periode : <strong>{format(stripeSub.renew, "d MMMM yyyy 'a' HH:mm", { locale: fr })}</strong>
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 1 }}>
              <Button variant="contained" size="small" onClick={openPortal} disabled={portalLoading}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>
                {portalLoading ? 'Ouverture...' : 'Gerer la facturation'}
              </Button>
              <Button component={Link} to="/bills" variant="outlined" size="small"
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>
                Mes factures
              </Button>
              {isTeamFeatureEnabled() && (
                <Button component={Link} to="/team" variant="outlined" size="small"
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>
                  Mon equipe
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ============================================================= */}
      {/* 3. MOBILE SUBSCRIPTION (si actif)                             */}
      {/* ============================================================= */}
      {hasMobileSub && (
        <Card sx={{ mb: 3, border: '1px solid rgba(0,0,0,0.06)' }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <PhoneIphoneIcon sx={{ color: '#64748b' }} />
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Abonnement mobile ({subscription.isIos ? 'iOS' : 'Android'})</Typography>
              {(subscription.expirationDate ?? 0) > 0 && (
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  Valide jusqu'au {format(new Date((subscription.expirationDate ?? 0) * 1000), "d MMMM yyyy", { locale: fr })}
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ============================================================= */}
      {/* 4. DISTRIBUTOR / OFFERED SUBSCRIPTION                         */}
      {/* ============================================================= */}
      {hasDistributorSub && (
        <Card sx={{ mb: 3, border: '1px solid rgba(0,0,0,0.06)' }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <CardGiftcardIcon sx={{ color: '#64748b' }} />
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Licence geree par un tiers</Typography>
              {((subscription as any).offerByDisplayName || (subscription as any).offerByEmail) && (
                <Typography variant="body2" sx={{ color: '#475569' }}>
                  Gestionnaire : <strong>{[(subscription as any).offerByDisplayName, (subscription as any).offerByEmail].filter(Boolean).join(' — ')}</strong>
                </Typography>
              )}
              {(subscription.expirationDateDistributor ?? 0) > 0 && (
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  Valide jusqu'au {format(new Date((subscription.expirationDateDistributor ?? 0) * 1000), "d MMMM yyyy", { locale: fr })}
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ============================================================= */}
      {/* 5. TARIFS (toujours visible)                                  */}
      {/* ============================================================= */}
      <Card sx={{ mb: 3, border: '1px solid rgba(0,0,0,0.06)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <GroupIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Tarifs</Typography>
          </Box>

          {loadingPrices ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CircularProgress size={18} />
              <Typography sx={{ color: '#64748b' }}>Chargement des tarifs...</Typography>
            </Box>
          ) : tierDisplay ? (
            <>
              {tierDisplay.headline && (
                <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: '#0f172a', mb: 1 }}>
                  {tierDisplay.headline}
                </Typography>
              )}
              <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                Tarif degressif selon le nombre de membres
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              {tierDisplay.rows.map((row, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
                  <Typography variant="body2" sx={{ color: '#475569' }}>{row.label}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>{row.price}</Typography>
                </Box>
              ))}
            </>
          ) : prices[0] && prices[0].unit_amount != null ? (
            <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: '#0f172a' }}>
              {(prices[0].unit_amount / 100).toFixed(2)} {(prices[0].currency ?? 'eur').toUpperCase() === 'EUR' ? '\u20ac' : prices[0].currency}
              {' / '}{prices[0].recurring?.interval === 'year' ? 'an' : 'mois'}
            </Typography>
          ) : (
            <Typography sx={{ color: '#94a3b8' }}>Aucun tarif configure.</Typography>
          )}
        </CardContent>
      </Card>

      {/* ============================================================= */}
      {/* 6. ACTIONS                                                    */}
      {/* ============================================================= */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        {/* "M'abonner" — visible si aucun acces actif (ni web, ni mobile, ni licence) */}
        {!hasWebSub && !hasMobileSub && !hasDistributorSub && (
          <Button
            variant="contained"
            onClick={subscribeMyself}
            disabled={checkoutLoading || !effectivePriceId || !user?.email}
            startIcon={checkoutLoading ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon />}
            sx={{
              textTransform: 'none', fontWeight: 700, borderRadius: 2, px: 3, py: 1.2,
              backgroundColor: '#4277b9', '&:hover': { backgroundColor: '#3666a3' }
            }}>
            {checkoutLoading ? 'Redirection...' : "M'abonner (1 siege)"}
          </Button>
        )}

        {/* "Ajouter des membres" — visible si abo web actif OU pas d'abo (creer un nouveau checkout) */}
        {isTeamFeatureEnabled() && (
          <Button
            variant={hasWebSub ? 'contained' : 'outlined'}
            onClick={() => navigate('/team/add')}
            startIcon={<PersonAddIcon />}
            sx={{
              textTransform: 'none', fontWeight: 700, borderRadius: 2, px: 3, py: 1.2,
              ...(hasWebSub ? { backgroundColor: '#4277b9', '&:hover': { backgroundColor: '#3666a3' } } : {})
            }}>
            {hasWebSub ? 'Ajouter des membres' : 'Abonner ma team'}
          </Button>
        )}
      </Box>

      {/* Error */}
      {error && (
        <Typography color="error" sx={{ mb: 2, fontWeight: 500 }}>{error}</Typography>
      )}
    </Box>
  )
}
